import React, { useEffect } from "react";
import { companyInfo } from "../../data/mockData";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const CookieRichtlinie = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#f5f5f7] border-b border-[#e5e5e5]">
        <div className="max-w-[800px] mx-auto px-6 py-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-[#4a7c59] hover:underline mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Zurück zur Startseite
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight"
          >
            Cookie-Richtlinie
          </motion.h1>
          <p className="text-[#6e6e73] mt-3 text-sm">
            Zuletzt aktualisiert: Juli 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="max-w-[800px] mx-auto px-6 py-16"
      >
        <div className="prose-custom">
          <Section title="Was sind Cookies?">
            <P>
              Cookies sind kleine Textdateien, die auf Ihrem Computer oder mobilen Gerät gespeichert werden,
              wenn Sie eine Website besuchen. Sie werden häufig verwendet, um Websites zum Funktionieren zu
              bringen oder effizienter zu machen, sowie um den Betreibern der Website Informationen zu
              liefern.
            </P>
          </Section>

          <Section title="Welche Cookies verwenden wir?">
            <H3>Notwendige Cookies</H3>
            <P>
              Diese Cookies sind für das Funktionieren der Website unbedingt erforderlich. Sie ermöglichen
              grundlegende Funktionen wie die Seitennavigation und den Zugang zu geschützten Bereichen der
              Website. Ohne diese Cookies kann die Website nicht ordnungsgemäß funktionieren.
            </P>
            <CookieTable
              cookies={[
                {
                  name: "cookie_consent",
                  zweck: "Speichert Ihre Cookie-Einwilligungseinstellung",
                  dauer: "1 Jahr",
                  typ: "Notwendig",
                },
              ]}
            />

            <H3>Funktionale Cookies</H3>
            <P>
              Diese Cookies ermöglichen es der Website, erweiterte Funktionalität und Personalisierung
              bereitzustellen. Sie können von uns oder von Drittanbietern gesetzt werden, deren Dienste wir
              auf unseren Seiten nutzen.
            </P>

            <H3>Analyse-Cookies</H3>
            <P>
              Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie
              Informationen anonym sammeln und melden. Sie helfen uns, die Website zu verbessern.
            </P>
          </Section>

          <Section title="Wie können Sie Cookies kontrollieren?">
            <P>
              Sie können Ihre Cookie-Einstellungen jederzeit ändern. Die meisten Webbrowser erlauben eine
              gewisse Kontrolle der meisten Cookies über die Browsereinstellungen. Um mehr über Cookies zu
              erfahren, einschließlich wie Sie sehen können, welche Cookies gesetzt wurden, besuchen Sie{" "}
              <a
                href="https://www.aboutcookies.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4a7c59] hover:underline font-medium"
              >
                www.aboutcookies.org
              </a>
              .
            </P>
            <P>So verwalten Sie Cookies in gängigen Browsern:</P>
            <ul className="list-disc list-inside space-y-2 text-[#424245] text-[15px] leading-relaxed mb-6">
              <li>
                <strong>Google Chrome:</strong> Einstellungen → Datenschutz und Sicherheit → Cookies
              </li>
              <li>
                <strong>Mozilla Firefox:</strong> Einstellungen → Datenschutz & Sicherheit → Cookies
              </li>
              <li>
                <strong>Safari:</strong> Einstellungen → Datenschutz → Cookies verwalten
              </li>
              <li>
                <strong>Microsoft Edge:</strong> Einstellungen → Cookies und Websiteberechtigungen
              </li>
            </ul>
            <P>
              Bitte beachten Sie, dass das Blockieren einiger Arten von Cookies Ihre Erfahrung auf unserer
              Website und die von uns angebotenen Dienste beeinträchtigen kann.
            </P>
          </Section>

          <Section title="Änderungen an dieser Richtlinie">
            <P>
              Wir können diese Cookie-Richtlinie von Zeit zu Zeit aktualisieren, um Änderungen an den von
              uns verwendeten Cookies oder aus anderen betrieblichen, rechtlichen oder regulatorischen
              Gründen widerzuspiegeln. Bitte besuchen Sie diese Cookie-Richtlinie regelmäßig, um über
              unsere Verwendung von Cookies und verwandten Technologien informiert zu bleiben.
            </P>
          </Section>

          <Section title="Kontakt">
            <P>
              Wenn Sie Fragen zu unserer Verwendung von Cookies oder anderen Technologien haben, kontaktieren
              Sie uns bitte unter:
            </P>
            <P>
              {companyInfo.fullName}
              <br />
              {companyInfo.address}
              <br />
              E-Mail: {companyInfo.email}
              <br />
              Telefon: {companyInfo.phone}
            </P>
          </Section>
        </div>
      </motion.div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight mb-6">{title}</h2>
    {children}
  </div>
);

const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold text-[#1d1d1f] mt-6 mb-3">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-[#424245] text-[15px] leading-relaxed mb-4">{children}</p>
);

const CookieTable = ({ cookies }) => (
  <div className="my-6 overflow-x-auto">
    <table className="w-full text-left border-collapse">
      <thead>
        <tr className="border-b border-[#e5e5e5]">
          <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider pb-3 pr-6">
            Cookie
          </th>
          <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider pb-3 pr-6">
            Zweck
          </th>
          <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider pb-3 pr-6">
            Dauer
          </th>
          <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider pb-3">
            Typ
          </th>
        </tr>
      </thead>
      <tbody>
        {cookies.map((cookie, i) => (
          <tr key={i} className="border-b border-[#f0f0f0]">
            <td className="text-sm text-[#1d1d1f] font-mono py-3 pr-6">{cookie.name}</td>
            <td className="text-sm text-[#424245] py-3 pr-6">{cookie.zweck}</td>
            <td className="text-sm text-[#424245] py-3 pr-6">{cookie.dauer}</td>
            <td className="py-3">
              <span className="text-xs font-medium text-[#4a7c59] bg-[#4a7c59]/8 px-2.5 py-1 rounded-full">
                {cookie.typ}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default CookieRichtlinie;
